import { useMemo, useState, type ReactNode } from 'react'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import CalendarViewDayRoundedIcon from '@mui/icons-material/CalendarViewDayRounded'
import CalendarViewWeekRoundedIcon from '@mui/icons-material/CalendarViewWeekRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import TodayRoundedIcon from '@mui/icons-material/TodayRounded'
import { IconButton, Pressable, RailChipTabs } from '../Common'
import { BottomSheet } from '../Common/BottomSheet'
import { useNls } from '../../nls/nlsProvider'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperMeetingItem } from '../../services/helper/helperMeetingService'
import {
  addDays,
  buildHelperCalendarModel,
  buildRangeDates,
  dayKey,
  formatMeetingTime,
  startOfDay,
  STATUS_DOT,
  statusLabel,
  weekKey,
  type CalendarChecklistEntry,
  type ViewMode,
  type WeeklyChecklistEntry,
} from './HelperCalendarModel'
import { occurrenceKeyFor } from '../../services/recurringChecklistService'
import { useChecklistLockState, type LockTarget } from '../../hooks/useChecklistLockState'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'


type Props = {
  items: HelperAssignedItem[]
  meetings?: HelperMeetingItem[]
  onSelectItem: (item: HelperAssignedItem, occurrenceKey?: string | null) => void
}

const CalendarPill = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string | number
}) => (
  <div className="min-w-0 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-1.5 py-2">
    <div className="flex flex-col items-center gap-0.5 text-center text-[10px] font-black uppercase tracking-[0.08em] text-[var(--muted)]">
      <span className="shrink-0 text-[var(--muted)]">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </div>
    <p className="mt-1 text-center text-lg font-black text-[var(--text-strong)]">{value}</p>
  </div>
)

const ChecklistCard = ({
  entry,
  onSelectItem,
}: {
  entry: CalendarChecklistEntry
  onSelectItem: (item: HelperAssignedItem, occurrenceKey?: string | null) => void
}) => {
  const dot = entry.isLocked ? STATUS_DOT.LOCKED : (STATUS_DOT[entry.status] ?? 'var(--muted)')
  const isDaily = entry.kind === 'DAILY'
  return (
    <Pressable
      onClick={() => onSelectItem(entry.item, entry.occurrenceKey)}
      className={`group text-left rounded-2xl border px-2.5 py-2 transition-colors ${
        isDaily
          ? 'border-[color-mix(in_srgb,var(--success)_20%,transparent)] bg-[var(--success-soft)] hover:bg-[color-mix(in_srgb,var(--success)_15%,transparent)]'
          : 'border-[var(--border-subtle)] bg-[var(--surface-card)] hover:bg-[var(--surface-hover)]'
      }`}
      data-testid={isDaily ? 'helper-calendar-daily' : 'helper-calendar-event'}
      data-locked={entry.isLocked ? 'true' : undefined}
    >
      <div className="mb-1 flex min-w-0 items-center gap-1.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dot }} data-testid={entry.isLocked ? 'helper-calendar-lock-dot' : undefined} />
        <p className={`truncate text-[10px] font-black uppercase tracking-widest ${isDaily ? 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]' : 'text-[var(--muted)]'}`}>
          {entry.isLocked ? 'Locked' : (isDaily ? 'Daily' : statusLabel(entry.status))}
        </p>
      </div>
      <p className={`line-clamp-2 text-[12px] font-semibold leading-tight ${entry.completed ? 'text-[var(--muted)] line-through' : 'text-[var(--text-strong)]'}`}>
        {entry.title}
      </p>
      {isDaily ? (
        <p className="mt-1 truncate text-[10px] font-medium text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]">{statusLabel(entry.status)}</p>
      ) : null}
    </Pressable>
  )
}

const MeetingCard = ({ meeting }: { meeting: HelperMeetingItem }) => (
  <a
    href={meeting.joinUrl?.startsWith('https://') ? meeting.joinUrl : undefined}
    target={meeting.joinUrl?.startsWith('https://') ? '_blank' : undefined}
    rel="noreferrer"
    className="group rounded-2xl border border-[color-mix(in_srgb,var(--info)_20%,transparent)] bg-[var(--info-soft)] px-2.5 py-2 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--info)_15%,transparent)]"
    data-testid="helper-calendar-meeting"
  >
    <div className="mb-1 flex min-w-0 items-center gap-1.5">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--info)]" />
      <p className="truncate text-[10px] font-black uppercase tracking-widest text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]">
        {formatMeetingTime(meeting)}
      </p>
    </div>
    <p className="line-clamp-2 text-[12px] font-semibold leading-tight text-[var(--text-strong)]">
      {meeting.title}
    </p>
  </a>
)

const WeeklyFocus = ({
  entries,
  onSelectItem,
}: {
  entries: WeeklyChecklistEntry[]
  onSelectItem: (item: HelperAssignedItem, occurrenceKey?: string | null) => void
}) => {
  const { t } = useNls()
  if (entries.length === 0) return null
  return (
    <section className="rounded-[28px] border border-[color-mix(in_srgb,var(--accent)_20%,transparent)] bg-[var(--accent-soft)] p-3" data-testid="helper-calendar-weekly-focus">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl border border-[color-mix(in_srgb,var(--accent)_25%,transparent)] bg-[var(--surface-card)] text-[var(--accent)]" /* mosaic-keep: icon */>
            <CalendarViewWeekRoundedIcon style={{ fontSize: '20px' }} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))]">{t('NLSHelperCalendarWeeklyFocus')}</p>
            <p className="truncate text-sm font-bold text-[var(--text-strong)]">{entries.length === 1 ? t('NLSHelperCalendarRecurringChecklistOne') : t('NLSHelperCalendarRecurringChecklistMany', { count: entries.length })}</p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {entries.map((entry) => {
          const dot = STATUS_DOT[entry.status] ?? 'var(--accent)'
          return (
            <Pressable
              key={entry.key}
              onClick={() => onSelectItem(entry.item, entry.occurrenceKey)}
              className="min-w-[210px] rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] px-3 py-3 text-left transition hover:bg-[var(--surface-hover)]"
              data-testid="helper-calendar-weekly"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dot }} />
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))]">{entry.weekLabel}</span>
              </div>
              <p className={`line-clamp-2 text-sm font-bold leading-tight ${entry.completed ? 'text-[var(--muted)] line-through' : 'text-[var(--text-strong)]'}`}>
                {entry.item.title}
              </p>
              <p className="mt-2 text-[11px] font-semibold text-[var(--muted)]">{statusLabel(entry.status)}</p>
            </Pressable>
          )
        })}
      </div>
    </section>
  )
}

const DayColumn = ({
  date,
  items,
  meetings,
  isToday,
  isWeekView,
  onSelectItem,
}: {
  date: Date
  items: CalendarChecklistEntry[]
  meetings: HelperMeetingItem[]
  isToday: boolean
  isWeekView: boolean
  onSelectItem: (item: HelperAssignedItem, occurrenceKey?: string | null) => void
}) => (
  <div className="flex min-w-0 flex-col gap-2">
    <header
      className={`rounded-2xl border ${isWeekView ? 'px-1' : 'px-3'} py-2 text-center ${
        isToday
          ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--text-strong)] shadow-[var(--shadow-card)]'
          : 'border-[var(--border-subtle)] bg-[var(--surface-card)] text-[var(--muted)]'
      }`}
    >
      <p className="text-[10px] uppercase tracking-wider opacity-70 whitespace-nowrap">
        {date.toLocaleDateString(undefined, { weekday: 'short' })}
      </p>
      <p className={`mt-0.5 font-black ${isWeekView ? 'text-base' : 'text-lg'}`}>{date.getDate()}</p>
    </header>
    <div className="flex min-h-[120px] flex-1 flex-col gap-1.5">
      {items.length === 0 && meetings.length === 0 ? (
        <div className="grid min-h-[92px] place-items-center rounded-2xl border border-dashed border-[var(--border-subtle)] bg-[var(--surface-card)] px-2 text-center">
          <p className="text-[10px] font-semibold text-[var(--muted)]">—</p>
        </div>
      ) : (
        <>
          {meetings.map((meeting) => <MeetingCard key={meeting.id} meeting={meeting} />)}
          {items.map((entry) => (
            <ChecklistCard key={entry.key} entry={entry} onSelectItem={onSelectItem} />
          ))}
        </>
      )}
    </div>
  </div>
)

export const HelperCalendar = ({ items, meetings = [], onSelectItem }: Props) => {
  const { t } = useNls()
  const workspaceId = items[0]?.workspaceId
  const { timeZone } = useWorkspaceDay(workspaceId)
  const [view, setView] = useState<ViewMode>('WEEK')
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()))
  const [isLegendOpen, setIsLegendOpen] = useState(false)

  const range = useMemo(() => buildRangeDates(view, anchor), [view, anchor])

  const monthGrid = useMemo(() => {
    if (view !== 'MONTH') return [] as Date[][]
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    const leadingDays = firstOfMonth.getDay()
    const start = addDays(firstOfMonth, -leadingDays)
    return Array.from({ length: 6 }, (_, weekIndex) =>
      Array.from({ length: 7 }, (_, dayIndex) => addDays(start, weekIndex * 7 + dayIndex)),
    )
  }, [view, anchor])

  const visibleDates = view === 'MONTH' ? monthGrid.flat() : range

  const calendarTargets = useMemo<LockTarget[]>(() => {
    const list: LockTarget[] = []
    for (const item of items) {
      if (item.checklistType === 'DAILY') {
        for (const date of visibleDates) {
          list.push({ itemId: item.id, periodKey: occurrenceKeyFor('DAILY', date, timeZone) })
        }
      } else if (item.checklistType === 'WEEKLY') {
        for (const date of visibleDates) {
          list.push({ itemId: item.id, periodKey: occurrenceKeyFor('WEEKLY', date, timeZone) })
        }
      } else {
        for (const date of visibleDates) {
          list.push({ itemId: item.id, periodKey: dayKey(date, timeZone) })
        }
      }
    }
    return list
  }, [items, visibleDates, timeZone])

  const lockState = useChecklistLockState(items, calendarTargets, {
    workspaceId,
    timeZone,
  })

  const calendarModel = useMemo(
    () => buildHelperCalendarModel(items, meetings, visibleDates, timeZone, lockState.pages),
    [items, meetings, visibleDates, timeZone, lockState.pages],
  )

  const shift = (delta: number) => {
    if (view === 'DAY') setAnchor(addDays(anchor, delta))
    else if (view === 'THREE_DAY') setAnchor(addDays(anchor, delta * 3))
    else if (view === 'WEEK') setAnchor(addDays(anchor, delta * 7))
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1))
  }

  const todayKey = dayKey(startOfDay(new Date()), timeZone)
  const headerLabel = view === 'MONTH'
    ? anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : `${range[0]?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${range[range.length - 1]?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="helper-calendar">
      <header
        className="relative overflow-hidden rounded-[32px] border border-[var(--border-subtle)] bg-[var(--surface-card)] px-4 py-4"
      >
        <div className="flex items-center gap-3">
          <IconButton
            variant="tertiary"
            size="md"
            onClick={() => shift(-1)}
            className="shrink-0"
            label={t('NLSHelperCalendarPrevious')}
            icon={<ChevronLeftRoundedIcon style={{ fontSize: '22px' }} />}
          />
          <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
            <Pressable
              onClick={() => setAnchor(startOfDay(new Date()))}
              className="max-w-full truncate text-xl font-black leading-tight tracking-tight text-[var(--text-strong)]"
            >
              {headerLabel}
            </Pressable>
            {view === 'MONTH' ? (
              <IconButton
                variant="tertiary"
                size="sm"
                onClick={() => setIsLegendOpen(true)}
                label={t('NLSHelperCalendarLegendTitle')}
                aria-expanded={isLegendOpen}
                icon={<InfoOutlinedIcon style={{ fontSize: '16px' }} />}
              />
            ) : null}
          </div>
          <IconButton
            variant="tertiary"
            size="md"
            onClick={() => shift(1)}
            className="shrink-0"
            label={t('NLSHelperCalendarNext')}
            icon={<ChevronRightRoundedIcon style={{ fontSize: '22px' }} />}
          />
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          <CalendarPill icon={<FactCheckRoundedIcon style={{ fontSize: '14px' }} />} label={t('NLSHelperCalendarTasks')} value={calendarModel.individualCount} />
          <CalendarPill icon={<CalendarViewDayRoundedIcon style={{ fontSize: '14px' }} />} label={t('NLSHelperCalendarDaily')} value={calendarModel.dailyCount} />
          <CalendarPill icon={<CalendarViewWeekRoundedIcon style={{ fontSize: '14px' }} />} label={t('NLSHelperCalendarWeekly')} value={calendarModel.weeklyEntries.length} />
          <CalendarPill icon={<EventAvailableRoundedIcon style={{ fontSize: '14px' }} />} label={t('NLSHelperCalendarMeetings')} value={calendarModel.meetingCount} />
        </div>
      </header>

      <RailChipTabs<ViewMode>
        orientation="horizontal"
        ariaLabel={t('NLSHelperCalendarViews')}
        value={view}
        onChange={setView}
        className="w-full flex"
        items={(([
          { key: 'DAY', label: t('NLSHelperCalendarViewDay') },
          { key: 'THREE_DAY', label: t('NLSHelperCalendarViewThreeDay') },
          { key: 'WEEK', label: t('NLSHelperCalendarViewWeek') },
          { key: 'MONTH', label: t('NLSHelperCalendarViewMonth') },
        ] as const).map(({ key, label }) => ({
          key,
          label,
          testId: `helper-calendar-${key.toLowerCase()}`,
        })))}
      />

      {view !== 'MONTH' ? (
        <>
          <WeeklyFocus entries={calendarModel.weeklyEntries} onSelectItem={onSelectItem} />
          <div
            className={`grid min-h-0 flex-1 ${view === 'DAY' ? 'grid-cols-1 gap-2' : view === 'THREE_DAY' ? 'grid-cols-3 gap-2' : 'grid-cols-7 gap-1'}`}
            data-testid="helper-calendar-strip"
          >
            {range.map((date) => (
              <DayColumn
                key={dayKey(date)}
                date={date}
                items={calendarModel.dayEntries.get(dayKey(date)) ?? []}
                meetings={calendarModel.meetingEntries.get(dayKey(date)) ?? []}
                isToday={dayKey(date) === todayKey}
                isWeekView={view === 'WEEK'}
                onSelectItem={onSelectItem}
              />
            ))}
          </div>
        </>
      ) : (
        <div
          className="relative flex min-h-0 flex-1 flex-col gap-1.5 rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-3"
          data-testid="helper-calendar-month"
        >
          <div className="grid grid-cols-7 text-center text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, idx) => (
              <span key={idx} className="py-1">{label}</span>
            ))}
          </div>
          {monthGrid.map((week, weekIdx) => (
            <div key={weekIdx} className="grid grid-cols-7 gap-1">
              {week.map((date) => {
                const inMonth = date.getMonth() === anchor.getMonth()
                const bucket = calendarModel.dayEntries.get(dayKey(date)) ?? []
                const meetingBucket = calendarModel.meetingEntries.get(dayKey(date)) ?? []
                const weekBucket = calendarModel.weeklyEntries.filter((entry) => entry.occurrenceKey === weekKey(date))
                const isToday = dayKey(date) === todayKey
                const visibleStatuses = [
                  ...meetingBucket.map(() => 'MEETING'),
                  ...weekBucket.map((entry) => entry.isLocked ? 'LOCKED' : entry.status),
                  ...bucket.map((entry) => entry.isLocked ? 'LOCKED' : entry.status),
                ].slice(0, 4)
                return (
                  <Pressable
                    key={dayKey(date)}
                    aria-pressed={isToday}
                    onClick={() => {
                      setAnchor(date)
                      setView('DAY')
                    }}
                    className={`aspect-square rounded-2xl border text-[12px] transition-colors ${
                      isToday
                        ? 'border-transparent bg-gradient-to-r from-[var(--btn-primary-from)] to-[var(--btn-primary-to)] text-[var(--on-accent)] shadow-[var(--shadow-card)]'
                        : inMonth
                          ? 'border-[var(--border-subtle)] bg-[var(--surface-card)] text-[var(--text-strong)] hover:bg-[var(--surface-hover)]'
                          : 'border-transparent text-[var(--muted)]'
                    }`}
                  >
                    <span className="font-black">{date.getDate()}</span>
                    {visibleStatuses.length > 0 ? (
                      <div className="mt-1 flex items-center justify-center gap-0.5">
                        {visibleStatuses.map((status, idx) => (
                          <span
                            key={idx}
                            className="h-1 w-1 rounded-full"
                            style={{ backgroundColor: isToday ? 'var(--on-accent)' : status === 'MEETING' ? 'var(--info)' : STATUS_DOT[status] ?? 'var(--muted)' }}
                          />
                        ))}
                      </div>
                    ) : null}
                    {weekBucket.length > 0 ? (
                      <RepeatRoundedIcon className="mx-auto mt-1 text-[var(--accent)]" style={{ fontSize: '12px' }} />
                    ) : null}
                  </Pressable>
                )
              })}
            </div>
          ))}
        </div>
      )}

      <BottomSheet
        isOpen={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
        title={t('NLSHelperCalendarLegendTitle')}
      >
        <div className="flex flex-col gap-3 p-4 text-sm font-medium text-[var(--text-strong)]">
          <span className="flex items-center gap-2">
            <TodayRoundedIcon className="text-[var(--success)]" style={{ fontSize: '18px' }} /* mosaic-keep: icon */ />
            {t('NLSHelperCalendarLegendDaily')}
          </span>
          <span className="flex items-center gap-2">
            <RepeatRoundedIcon className="text-[var(--accent)]" style={{ fontSize: '18px' }} /* mosaic-keep: icon */ />
            {t('NLSHelperCalendarLegendWeekly')}
          </span>
          <span className="flex items-center gap-2">
            <AccessTimeRoundedIcon className="text-[var(--warning)]" style={{ fontSize: '18px' }} /* mosaic-keep: icon */ />
            {t('NLSHelperCalendarLegendMeetings')}
          </span>
        </div>
      </BottomSheet>
    </div>
  )
}
